<template>
  <section id="adoption-installation">
    <div class="section-container">
      <div class="left-aligned">
        <router-link
          to="/adoption"
          class="back-container"
        >
          Adoption
        </router-link>
      </div>
      <div class="a-header-container">
        <div class="page-label">
          Installations
          <span class="sac__font-12 f-white bold">({{ installations.length }})</span>
        </div>
        <div class="a-right-container">
          <button
            class="export-button"
            @click="exportInstallationCSV"
          >
            <span v-if="!exporting">
              EXPORT CSV
              <img
                src="@shared/assets/img/general/export-download.png"
                alt="Download"
              >
            </span>
            <span v-else>
              EXPORTING
              <img
                src="@shared/assets/img/general/loading-white.svg"
                alt="Exporting"
              >
            </span>
          </button>
          <SelectedPartner />
          <div class="search-container">
            <SACSearch
              darkMode
              :width="200"
              :height="40"
              :search="search"
              placeholder="Search"
              @input="(value) => updateSearch(value.trim())"
            />
          </div>
        </div>
      </div>
      <div class="body-container">
        <SACFilters
          :filters="adoptionInstallFilters"
          :change="updateFilters"
          :clear="clearFilter"
          :loading="loading"
          :disabled="adoptionInstallFilters.length === 0"
        />
        <InstallationBoard
          :search="search"
          :installations="installations"
          :loading="loading"
        />
      </div>
    </div>
  </section>
</template>

<script>
import moment from 'moment';

import adminMixin from '@shared/mixins/adminMixin';
import adoptionMixin from '@shared/mixins/adoptionMixin';

import InstallationBoard from '@shared/components/adoption/InstallationBoard.vue';

import apiClient from '@shared/lib/general/apiClient';
import exportToFile from '@shared/lib/util/exportToFile';
import SACSearch from '@shared/components/sac/SACSearch.vue';
import SACFilters from '@shared/components/sac/SACFilters.vue';
import initialState from '@shared/store/adoption/adoptionInitialState';
import SelectedPartner from '@shared/components/admin/SelectedPartner.vue';

export default {
  name: 'AdoptionInstallationView',
  metaInfo: {
    title: 'SmartAC.com | Installation',
  },
  components: {
    SelectedPartner,
    SACSearch,
    InstallationBoard,
    SACFilters,
  },
  mixins: [adminMixin, adoptionMixin],
  data() {
    return {
      search: '',
      showSearch: false,
      installations: [],
      loading: true,
      exporting: false,
    };
  },
  watch: {
    selectedCorpId() {
      if (!this.loading) {
        this.initialLoad();
      }
    },
    adoptionInstallFilters() {
      if (!this.loading) {
        this.getInstallations();
      }
    },
  },
  mounted() {
    this.initialLoad();
  },
  methods: {
    async initialLoad() {
      const {
        query,
      } = this.$route;
      this.$store.dispatch('resetInstallFilters');
      await this.getTechnicians();
      if (query.unverified) {
        const newFilters = this.adoptionInstallFilters.map((filter) => {
          const newFilter = {
            ...filter,
          };
          if (filter.key === 'isVerified') {
            newFilter.active = true;
            newFilter.activeOption = {
              label: 'Unverified Install',
              value: false,
            };
          }
          return newFilter;
        });
        this.updateFilters(newFilters);
      }
      if (query.techId) {
        const newFilters = this.adoptionInstallFilters.map((filter) => {
          const newFilter = {
            ...filter,
          };
          if (filter.key === 'techs') {
            const tech = filter.options.find((o) => Number(o.value) === Number(query.techId));
            newFilter.active = true;
            newFilter.activeOption = tech;
          }
          return newFilter;
        });
        this.updateFilters(newFilters);
      }
      this.getInstallations();
    },
    updateSearch(value) {
      this.search = value;
    },
    handleChange(field, value, refreshLink) {
      this.updateFilters(initialState().adoptionInstallFilters);
      const {
        query: {
          technician,
        },
      } = this.$route;
      if (technician && refreshLink) {
        this.$router.replace('/adoption/installations');
      }
      this.$store.dispatch('changeAdoptionValues', [{
        field,
        value,
      }]);
    },
    updateFilters(filters) {
      this.$store.dispatch('changeAdoptionValues', [{
        field: 'adoptionInstallFilters',
        value: filters,
      }]);
    },
    clearFilter(key) {
      const newFilters = this.adoptionInstallFilters.map((filter) => {
        if (filter.key === key) {
          filter.active = false;
          filter.activeOption = {};
        }
        return filter;
      });
      this.updateFilters(newFilters);
    },
    // TODO: move api call to actions
    async getInstallations() {
      this.loading = true;
      try {
        const url = this.formInstallationUrl(`/adoption/stat/installations/corp/${this.selectedCorpId}`);
        const { data } = await apiClient({
          method: 'GET',
          url,
        });
        let installs = data;//.filter((item) => !item.nonInstaller);
        this.adoptionInstallFilters.forEach((filter) => {
          const {
            active,
            key,
            activeOption: {
              value,
            },
          } = filter;
          if (active) {
            if (key === 'isVerified' && value === 'pending') {
              // show pending
              installs = data.filter((item) => !item.lastVerifiedDate);
            } else if (
              key === 'isVerified' && !value
            ) {
              // remove pending from unverified list
              installs = data.filter((item) => item.lastVerifiedDate);
            }
          }
        });
        this.installations = installs;
      } catch (err) {
        console.error(err);
      }
      this.loading = false;
    },
    async getTechnicians() {
      const {
        query: {
          technician,
        },
      } = this.$route;
      try {
        const { data } = await apiClient({
          method: 'GET',
          url: `/technicians/all/${this.selectedCorpId}`,
        });
        const newFilters = this.adoptionInstallFilters.map((filter) => {
          const newFilter = {
            ...filter,
          };
          if (filter.key === 'techs') {
            let activeOption = {};
            const techOptions = data.filter((item) => !item.nonInstaller).map((tech) => {
              const option = {
                label: `${tech.firstName} ${tech.lastName}`,
                value: tech.id,
              };
              if (technician && Number(tech.id) === Number(technician)) {
                activeOption = option;
              }
              return option;
            });
            newFilter.options = techOptions.sort((a, b) => {
              if (a.label.toLowerCase() > b.label.toLowerCase()) {
                return 1;
              }
              if (a.label.toLowerCase() < b.label.toLowerCase()) {
                return -1;
              }
              return 0;
            });
            if (activeOption.value) {
              newFilter.activeOption = activeOption;
              newFilter.active = true;
            }
          }
          return newFilter;
        });
        this.updateFilters(newFilters);
      } catch (err) {
        console.error(err);
      }
    },
    async exportInstallationCSV() {
      this.exporting = true;
      try {
        const url = this.formInstallationUrl(`/adoption/stat/installations/export/corp/${this.selectedCorpId}`);
        const { data } = await apiClient({
          method: 'GET',
          url,
        });
        exportToFile({
          data,
          filename: 'installation.csv',
        });
      } catch (err) {
        console.error(err);
      }
      this.exporting = false;
    },
    formInstallationUrl(baseUrl) {
      const {
        adoptionInstallFilters,
      } = this;
      let url = baseUrl+'?isConnect=false';
      adoptionInstallFilters.forEach((filter) => {
        const {
          active,
          key,
          activeOption: {
            value,
          },
        } = filter;
        if (active) {
          if (!url.includes('?')) {
            url += '?';
          } else {
            url += '&';
          }
          if (key === 'techs') {
            url += `technicianId=${value}`;
          }
          if (key === 'nameplate') {
            url += `profiled=${value === 'profiled'}`;
          }
          if (key === 'installDate') {
            let start = null;
            const end = moment().format('YYYY-MM-DD');
            if (value === 'lastWeek') {
              start = moment().subtract(6, 'days').format('YYYY-MM-DD');
            } else if (value === 'lastMonth') {
              start = moment().subtract(29, 'days').format('YYYY-MM-DD');
            } else if (value === 'lastYear') {
              start = moment().subtract(364, 'days').format('YYYY-MM-DD');
            }
            url += `start=${start}&end=${end}`;
          }
          if (key === 'isVerified' && value !== 'all') {
            url += `isVerified=${value}`;
          }
        }
      });
      return url;
    },
  },
};
</script>

<style lang="scss" scoped>
section#adoption-installation {
  display: flex;
  flex: 1;
  background: $BLACK;
  letter-spacing: 0.2px;
  justify-content: center;
  padding: 52px 100px 36px;

  @media (max-width: 1279px) {
    padding: 52px 60px 0;
  }

  @media (max-width: 1023px) {
    padding: 52px 40px 0;
  }

  .section-container {
    display: grid;
    align-content: flex-start;
    grid-template-rows: auto auto auto 1fr;
    width: 1300px;

    @media (max-width: 1280px) {
      width: 100%;
      font-size: 90%;
      gap: 12px;
    }

    @media (max-width: 1023px) {
      font-size: 80%;
    }

    @media (max-width: 850px) {
      font-size: 70%;
    }

    .back-container {
      font-weight: 700;
      line-height: 1;
      color: rgba(139, 169, 255, 1);
      display: flex;
      align-items: center;
    }

    .left-aligned {
      display: grid;
      justify-self: flex-start;
    }

    .a-header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .a-right-container {
        display: flex;
        align-items: center;
        gap: 16px;

        .search-box {
          z-index: 3;
          margin-right: 24px;
        }

        .export-button {
          height: 38px;
          padding: 0 8px;
          color: $N50;
          display: flex;
          align-items: center;
          white-space: nowrap;
          font-size: 0.875rem;
          font-weight: 700;
          border: 1px solid $N50;
          border-radius: 8px;
          line-height: 1;

          span {
            height: 100%;
            display: flex;
            align-items: center;
          }

          img {
            height: 40%;
            margin-left: 8px;
            filter: invert(50%);
          }
        }
      }
    }
  }
}
</style>
